<template>
  <b-row class="justify-content-center align-items-center h-100 m-0">
    <b-col v-if="resetSuccess" cols="auto" sm="6" md="4" lg="4" xl="3">
      <div>
        <img src="@/assets/4life-logo.svg" class="mb-4" height="60" />
        <h5 class="display-4 mt-1 mb-2 font-weight-bold">
          Your password has been successfully reset
        </h5>
        <p class="text-black-50">You can now sign in with you new password.</p>
      </div>

      <b-button class="w-100 mt-4" variant="outline-primary" @click="goToSignIn()"
        >Go to sign in page</b-button
      >
    </b-col>

    <b-col v-else cols="auto" sm="6" md="4" lg="4" xl="3">
      <b-form @submit.prevent="onClickReset">
        <div>
          <img src="@/assets/4life-logo.svg" class="mb-4" height="60" />
          <h5 class="display-4 mt-1 mb-2 font-weight-bold">Reset your password</h5>
          <p class="text-black-50">Choose a new password.</p>
        </div>

        <div class="mt-5">
          <b-form-group label="New password">
            <b-form-input
              id="input-reset-new-password"
              v-model="$v.password.$model"
              name="input-reset-new-password"
              :type="`${showPassword ? 'text' : 'password'}`"
              class="mb-1"
              placeholder="Enter your new password"
              :disabled="isLoading"
              :state="$v.password.$dirty && isSubmit ? !$v.password.$error : null"
              disable-value-track
            />

            <b-form-invalid-feedback
              v-if="isSubmit && !$v.password.required"
              ref="text__password-required"
            >
              {{ textErrorPassword.required }}
            </b-form-invalid-feedback>

            <b-form-invalid-feedback
              v-else-if="isSubmit && !$v.password.minLength"
              ref="text__password-too-short"
            >
              {{ textErrorPassword.minLength }}
            </b-form-invalid-feedback>

            <b-form-invalid-feedback
              v-else-if="isSubmit && !$v.password.sameAsPassword"
              ref="text__password-not-match"
            >
              {{ textErrorPassword.sameAsPassword }}
            </b-form-invalid-feedback>

            <small v-else class="text-black-50">Password must be at least 6 characters.</small>
          </b-form-group>
        </div>

        <div>
          <b-form-group label="Confirm password">
            <b-form-input
              id="input-reset-confirm-password"
              v-model="$v.confirmPassword.$model"
              name="input-reset-confirm-password"
              :type="`${showPassword ? 'text' : 'password'}`"
              placeholder="Enter your new password"
              :disabled="isLoading"
              :state="$v.confirmPassword.$dirty && isSubmit ? !$v.confirmPassword.$error : null"
              disable-value-track
            />

            <b-form-invalid-feedback
              v-if="isSubmit && !$v.confirmPassword.required"
              ref="text__comfirm-password-required"
            >
              {{ textErrorConfirmPassword.required }}
            </b-form-invalid-feedback>

            <b-form-invalid-feedback
              v-else-if="isSubmit && !$v.confirmPassword.minLength"
              ref="text__comfirm-password-too-short"
            >
              {{ textErrorConfirmPassword.minLength }}
            </b-form-invalid-feedback>

            <b-form-invalid-feedback
              v-else-if="isSubmit && !$v.confirmPassword.sameAsPassword"
              ref="text__comfirm-password-not-match"
            >
              {{ textErrorConfirmPassword.sameAsPassword }}
            </b-form-invalid-feedback>
          </b-form-group>
        </div>

        <div>
          <b-form-checkbox
            v-model="showPassword"
            aria-describedby="flavours"
            aria-controls="flavours"
          >
            Show password
          </b-form-checkbox>
        </div>

        <b-button
          id="btn__reset-password"
          ref="btn__reset-password"
          name="btn__reset-password"
          type="submit"
          class="w-100 btn__reset-password mt-4"
          variant="primary"
          @click="onClickReset"
        >
          <span v-if="!isLoading">Reset</span>
          <span v-else class="pr-2 pl-2">
            <b-spinner label="Loading..." variant="secondary" small />
          </span>
        </b-button>

        <p class="text-black-50 mt-4">
          Know your password?
          <a href="javascript:void(0);" @click="goToSignIn()">Try Signing in</a>
        </p>
      </b-form>
    </b-col>
  </b-row>
</template>

<script>
import { required, minLength, sameAs } from 'vuelidate/lib/validators';
import User from '@/models/User';
import { notificationMixin } from '@/mixins/notificationMixin';
import { amplitudeMixin } from '@/mixins/amplitudeMixin';

export default {
  mixins: [notificationMixin, amplitudeMixin],

  data() {
    return {
      password: null,
      confirmPassword: null,
      isSubmit: false,
      isLoading: false,
      showPassword: false,
      resetSuccess: false
    };
  },

  validations() {
    return {
      password: {
        required,
        minLength: minLength(6),
        sameAsPassword: sameAs('confirmPassword')
      },
      confirmPassword: {
        required,
        minLength: minLength(6),
        sameAsPassword: sameAs('password')
      }
    };
  },

  computed: {
    token() {
      return this.$route.query.token || '';
    },

    textErrorPassword() {
      return {
        required: 'New password is required.',
        minLength: 'New password is too short.',
        sameAsPassword: 'New password and confirm password must be matched.'
      };
    },

    textErrorConfirmPassword() {
      return {
        required: 'Confirm password is required.',
        minLength: 'Confirm password is too short.',
        sameAsPassword: 'Confirm password and password must be matched.'
      };
    }
  },

  methods: {
    async onClickReset() {
      this.isSubmit = true;
      this.$v.$touch();

      if (this.$v.password.$anyError && this.$v.confirmPassword.$anyError) {
        let errorNewPassword, errorConfirmPassword;

        if (!this.$v.password.required) {
          errorNewPassword = this.textErrorPassword.required;
        }

        if (!this.$v.password.sameAsPassword) {
          errorNewPassword = this.textErrorPassword.sameAsPassword;
        }

        if (!this.$v.password.minLength) {
          errorNewPassword = this.textErrorPassword.minLength;
        }

        if (!this.$v.confirmPassword.required) {
          errorConfirmPassword = this.textErrorConfirmPassword.required;
        }

        if (!this.$v.confirmPassword.sameAsPassword) {
          errorConfirmPassword = this.textErrorConfirmPassword.sameAsPassword;
        }

        if (!this.$v.confirmPassword.minLength) {
          errorConfirmPassword = this.textErrorConfirmPassword.minLength;
        }

        this.$_amplitudeMixin_onCheck({
          IsFormInvalid: true,
          InvalidState: {
            newPassword: errorNewPassword,
            confirmPassword: errorConfirmPassword
          }
        });
        return;
      } else {
        try {
          this.isLoading = true;

          this.$_amplitudeMixin_onCheck({
            IsFormInvalid: false,
            InvalidState: {}
          });

          const { response } = await User.api().resetPassword({
            token: this.token,
            password: this.password
          });
          if (response && response.data && response.data == 'reset password success') {
            this.resetSuccess = true;
          }
        } catch (error) {
          this.notiError(error.response.data.message);
        } finally {
          this.isLoading = false;
        }
      }
    },

    goToSignIn() {
      this.$router.push({ name: 'SignIn' });
    }
  }
};
</script>

<style lang="scss" scoped>
$blue: #4191ff;

.btn__reset-password {
  color: #fff;
  background-color: $blue;
  border-color: $blue;
  border: 2px solid $blue;
  box-shadow: 0px 3.52px 8.4px rgba(65, 145, 255, 0.4), 0px 1px 6.16px rgba(65, 145, 255, 0.54);
  border-radius: 3.2px;
}

.form-control.is-invalid {
  background-image: none !important;
}
.form-control.is-valid {
  background-image: none !important;
}
</style>
